import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './CellContextMenu.module.scss';

export interface CellContextMenuItem {
    label: string;
    onClick: () => void;
    danger?: boolean;
    divider?: false;
}

export interface CellContextMenuDivider {
    divider: true;
}

export type CellContextMenuEntry = CellContextMenuItem | CellContextMenuDivider;

interface CellContextMenuProps {
    x: number;
    y: number;
    entries: CellContextMenuEntry[];
    onClose: () => void;
}

export function CellContextMenu({ x, y, entries, onClose }: CellContextMenuProps) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleDocClick(e: any) {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        }
        function handleKey(e: any) {
            if (e.key === 'Escape') onClose();
        }
        document.addEventListener('mousedown', handleDocClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleDocClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [onClose]);

    // Clamp to viewport so the menu doesn't fly off the right/bottom edge.
    const MENU_W = 220;
    const MENU_H = entries.length * 32 + 16; // rough estimate
    const left = Math.min(x, (typeof window !== 'undefined' ? window.innerWidth : 1024) - MENU_W - 8);
    const top = Math.min(y, (typeof window !== 'undefined' ? window.innerHeight : 768) - MENU_H - 8);

    const node = (
        <div
            ref={ref}
            className={styles.menu}
            style={{ left, top }}
            role="menu"
            onMouseDown={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
        >
            {entries.map((entry, i) => {
                if ('divider' in entry && entry.divider) {
                    return <div key={`d-${i}`} className={styles.divider} aria-hidden />;
                }
                const item = entry as CellContextMenuItem;
                return (
                    <button
                        key={item.label}
                        type="button"
                        className={`${styles.item} ${item.danger ? styles.itemDanger : ''}`}
                        role="menuitem"
                        onClick={() => {
                            item.onClick();
                            onClose();
                        }}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );

    if (typeof document === 'undefined') return null;
    return createPortal(node, document.body);
}
